import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faChevronLeft, faGear } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Toast, Toaster } from '@repo/videa-components/src/index';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import type { ZuubOnboardingBeginResponse } from '@/@types/zuub/contracts';
import { selectedPayerIdAtom } from '@/modules/autoVerify/atoms';
import { useZuubRcmMessage } from '@/react-query/rcm/useZuubRcmMessages';
import {
  useZuubRcmOnboardingBegin,
  useZuubRcmOnboardingPayers,
  useZuubRcmOnboardingStatuses,
} from '@/react-query/rcm/useZuubRcmOnboarding';
import routes from '@/routes/routes.constants';

import { CredentialsFailedError } from '../CredentialsFailedError';

import { getPayerStatusChipProps } from './helpers/helpers';
import { ConnectionErrorBanner } from './mainContent/ConnectionErrorBanner';
import { ConnectionInstructions } from './mainContent/ConnectionInstructions';
import { Payer2FAMessages } from './mainContent/Payer2FAMessages';
import { PayerHeader } from './mainContent/PayerHeader';
import { TestConnectionButton } from './mainContent/TestConnectionButton';
import { VideaUserInformationForm } from './mainContent/VideaUserInformationForm';
import { VideaUserInformationFormSkeleton } from './mainContent/VideaUserInformationFormSkeleton';
import { PayerListSidebar } from './sidebar/PayerListSidebar';

// Per-payer state interface
interface PayerState {
  onboardingResponse: ZuubOnboardingBeginResponse | null;
  onboardingError: Error | null;
  isLoadingOnboarding: boolean;
  localPassword: string;
  isLocalTestingConnection: boolean;
  connectionError: Error | null;
  serverMessage: string | null;
  errorCode: string | null;
}

// Default payer state
const DEFAULT_PAYER_STATE: PayerState = {
  onboardingResponse: null,
  onboardingError: null,
  isLoadingOnboarding: false,
  localPassword: '',
  isLocalTestingConnection: false,
  connectionError: null,
  serverMessage: null,
  errorCode: null,
};

export const ConnectToPayerPortalPage = () => {
  // hooks
  const history = useHistory();
  const { t } = useLingui();
  const { data: payers } = useZuubRcmOnboardingPayers();
  const beginOnboardingMutation = useZuubRcmOnboardingBegin();

  // atoms
  const [selectedPayerId, setSelectedPayerId] = useAtom(selectedPayerIdAtom);

  // Per-payer state management using Map
  const [payerStates, setPayerStates] = useState<Map<number, PayerState>>(
    new Map(),
  );

  // Get payer IDs for status fetch - memoize to prevent unnecessary hook recreations
  const payerIds = useMemo(() => {
    return payers?.map((p) => p.id) || [];
  }, [payers]);

  // Track which payers are actively being tested (for polling)
  // Only poll payers where user has clicked "Test connection"
  const testingPayerIds = useMemo(() => {
    const testing = new Set<number>();
    payerStates.forEach((state, payerId) => {
      if (state.isLocalTestingConnection) {
        testing.add(payerId);
      }
    });
    return testing;
  }, [payerStates]);

  // Fetch individual statuses for all payers using useQueries
  // On page load, fetches once (no polling)
  // Only polls payers that are in testingPayerIds (user clicked "Test connection")
  const {
    data: payerStatusData,
    loadingStates,
    refetchPayer,
  } = useZuubRcmOnboardingStatuses(
    payerIds,
    Boolean(payers && payers.length > 0),
    testingPayerIds,
  );

  // Poll 2FA messages for the selected payer every 5 seconds
  const { data: selectedPayerMessages, isLoading: isMessagesLoading } =
    useZuubRcmMessage(selectedPayerId);

  // Track previous statuses to detect changes
  const previousStatusesRef = useRef<Record<string, string | null>>({});

  // Helper function to show connection status toasts
  const showConnectionToast = useCallback(
    (payerName: string, status: 'Succeeded' | 'Failed') => {
      if (status === 'Succeeded') {
        toast(
          <Toast
            variant="success"
            title={t`Successfully connected to ${payerName}!`}
          />,
        );
      } else {
        toast(
          <Toast
            variant="error"
            title={t`Connection to ${payerName} failed.`}
            description={t`Please try again.`}
          />,
        );
      }
    },
    [t],
  );

  const selectedPayer = payers?.find((p) => p.id === selectedPayerId);

  // Get or initialize state for selected payer
  const currentPayerState = useMemo(() => {
    if (!selectedPayerId) {
      return DEFAULT_PAYER_STATE;
    }
    return payerStates.get(selectedPayerId) || DEFAULT_PAYER_STATE;
  }, [selectedPayerId, payerStates]);

  const {
    onboardingResponse,
    onboardingError,
    serverMessage,
    errorCode,
    isLoadingOnboarding,
    localPassword,
    isLocalTestingConnection,
    connectionError,
  } = currentPayerState;

  // Helper to update payer state
  const updatePayerState = useCallback(
    (payerId: number, updates: Partial<PayerState>) => {
      setPayerStates((prev) => {
        const newMap = new Map(prev);
        const currentState = newMap.get(payerId) || DEFAULT_PAYER_STATE;
        newMap.set(payerId, { ...currentState, ...updates });
        return newMap;
      });
    },
    [], // setPayerStates is stable, no dependencies needed
  );

  // Refetch function for selected payer - uses the refetchPayer from useZuubRcmOnboardingStatuses
  const refetchStatus = useCallback(async () => {
    if (!selectedPayerId) return;
    return refetchPayer(selectedPayerId);
  }, [selectedPayerId, refetchPayer]);

  // Get status for selected payer to ensure consistency with sidebar
  // Use the same data source as the sidebar for consistent status display
  const selectedPayerStatus = selectedPayerId
    ? payerStatusData?.[selectedPayerId.toString()]
    : undefined;

  // Use the shared helper function to get consistent status chip props
  // This ensures the main page chip matches the sidebar chip exactly
  const statusChipProps = getPayerStatusChipProps(
    selectedPayerStatus,
    isLocalTestingConnection,
  );

  // Check if status is failed (for consistency with chip)
  const isFailedStatus = selectedPayerStatus?.credentialStatus === 'Failed';

  // Determine if button should be in loading/disabled state
  // Button is loading if:
  // - User clicked "Test Connection" (isLocalTestingConnection), OR
  // - Status chip shows "Connecting" (progress variant), OR
  // - API status is Acknowledged/InProgress
  // But NOT if status is Failed or there's a connection error
  const isButtonLoading = useMemo(() => {
    if (isFailedStatus || connectionError) return false;
    if (isLocalTestingConnection) return true;
    if (statusChipProps.variant === 'progress') return true;
    const status = selectedPayerStatus?.credentialStatus;
    return status === 'Acknowledged' || status === 'InProgress';
  }, [
    isLocalTestingConnection,
    statusChipProps.variant,
    selectedPayerStatus?.credentialStatus,
    isFailedStatus,
    connectionError,
  ]);

  // Sort payers the same way as the sidebar (alphabetically A-Z by default)
  const sortedPayers = useMemo(() => {
    if (!payers) return [];
    return [...payers].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [payers]);

  const handlePayerClick = useCallback(
    (payerId: number) => setSelectedPayerId(payerId),
    [setSelectedPayerId],
  );

  const handleSavePassword = async (password: string) => {
    if (!selectedPayerId) return;
    try {
      await beginOnboardingMutation.mutateAsync({
        payerId: selectedPayerId,
        password,
      });
      // Store the password locally since the endpoint doesn't return it
      updatePayerState(selectedPayerId, {
        localPassword: password,
      });
    } catch (error) {
      console.error('Failed to update onboarding data:', error);
    }
  };

  const handleSaveUsername = async (username: string) => {
    if (!selectedPayerId) return;
    try {
      await beginOnboardingMutation.mutateAsync({
        payerId: selectedPayerId,
        username,
      });
    } catch (error) {
      console.error('Failed to update username:', error);
    }
  };

  const handleSaveQuestions = async (
    payerId: number,
    questions: Array<{ question: string; answer: string }>,
  ) => {
    try {
      await beginOnboardingMutation.mutateAsync({
        payerId,
        questions,
      });
    } catch (error) {
      console.error('Failed to save security questions:', error);
      throw error;
    }
  };

  const handleTestConnection = async () => {
    if (!selectedPayerId) return;
    const payerIdStr = selectedPayerId.toString();

    // Capture current status before testing starts (for toast tracking)
    const currentStatusBeforeTest =
      payerStatusData?.[payerIdStr]?.credentialStatus ?? null;
    previousStatusesRef.current[payerIdStr] = currentStatusBeforeTest;

    // Mark payer as testing - this will be tracked for toast notifications
    updatePayerState(selectedPayerId, {
      isLocalTestingConnection: true,
      connectionError: null,
    });

    try {
      // Refetch the selected payer's status to trigger connection test
      await refetchStatus();
    } catch (error) {
      updatePayerState(selectedPayerId, {
        connectionError: error as Error,
        isLocalTestingConnection: false,
      });
    }
  };

  const handleBackToPatientList = () => {
    history.push('/auto-verify');
  };

  // Initialize selected payer - use atom value if set, otherwise default to first payer (after initial sorting)
  useEffect(() => {
    if (!sortedPayers || sortedPayers.length === 0) return;

    // If a payer is already selected (from atom), validate it exists in the list
    if (selectedPayerId !== null) {
      const isValidPayer = sortedPayers.some((p) => p.id === selectedPayerId);
      if (isValidPayer) {
        return; // Valid payer already selected, don't override
      }
      // Invalid payer ID in atom, clear it and fall through to select first payer
      setSelectedPayerId(null);
    }

    // No payer selected or invalid payer ID - select the first payer from sorted list
    setSelectedPayerId(sortedPayers[0].id);
  }, [sortedPayers, selectedPayerId, setSelectedPayerId]);

  // Make onboarding request when payer is selected (only if not already loaded or errored)
  useEffect(() => {
    if (
      selectedPayerId &&
      !currentPayerState.onboardingResponse &&
      !currentPayerState.onboardingError &&
      !currentPayerState.isLoadingOnboarding
    ) {
      updatePayerState(selectedPayerId, {
        isLoadingOnboarding: true,
        onboardingResponse: null,
        onboardingError: null,
        localPassword: '',
        isLocalTestingConnection: false,
        connectionError: null,
        serverMessage: null,
        errorCode: null,
      });

      beginOnboardingMutation.mutate(
        { payerId: selectedPayerId },
        {
          onSuccess: (response) => {
            updatePayerState(selectedPayerId, {
              onboardingResponse: response,
              onboardingError: null,
              isLoadingOnboarding: false,
            });
            // Don't refetch status here - clicking a payer shouldn't change the sidebar status
            // The status queries will update naturally when the actual status changes
          },
          onError: (error) => {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            let serverMessage: string | null = null;
            // eslint-disable-next-line @typescript-eslint/no-shadow
            let errorCode: string | null = null;
            if ((error as AxiosError).response?.status === 400) {
              const errorData = (error as AxiosError).response?.data as {
                message: string;
                code?: string;
              };
              serverMessage = errorData.message;
              errorCode = errorData.code || null;
            }
            updatePayerState(selectedPayerId, {
              onboardingError: error as Error,
              isLoadingOnboarding: false,
              serverMessage,
              errorCode,
            });
          },
        },
      );
    }
  }, [
    selectedPayerId,
    currentPayerState.onboardingResponse,
    currentPayerState.onboardingError,
    currentPayerState.isLoadingOnboarding,
    beginOnboardingMutation,
    updatePayerState,
  ]);

  // Track connection progress and show toasts on status changes
  useEffect(() => {
    if (!payerStatusData || !payers) return;

    // Initialize previous statuses on first load if not already set
    if (Object.keys(previousStatusesRef.current).length === 0) {
      const initialStatuses: Record<string, string | null> = {};
      payers.forEach((payer) => {
        const payerIdStr = payer.id.toString();
        const statusData = payerStatusData[payerIdStr];
        initialStatuses[payerIdStr] = statusData?.credentialStatus ?? null;
      });
      previousStatusesRef.current = initialStatuses;
      return; // Don't show toasts on initial load
    }

    const currentStatuses: Record<string, string | null> = {};
    const previousStatuses = previousStatusesRef.current;

    // Build current statuses map
    payers.forEach((payer) => {
      const payerIdStr = payer.id.toString();
      const statusData = payerStatusData[payerIdStr];
      currentStatuses[payerIdStr] = statusData?.credentialStatus ?? null;
    });

    // Check for status transitions from in-progress to final states
    payers.forEach((payer) => {
      const payerIdStr = payer.id.toString();
      const previousStatus = previousStatuses[payerIdStr];
      const currentStatus = currentStatuses[payerIdStr];

      // Skip if status hasn't changed
      if (previousStatus === currentStatus) return;

      // Determine if we were tracking this payer's connection progress
      const payerState = payerStates.get(payer.id);
      const isTesting = payerState?.isLocalTestingConnection ?? false;

      // Show toast if:
      // 1. We're actively testing this payer (isLocalTestingConnection is true), OR
      // 2. Previous status was in progress (Acknowledged or InProgress)
      // AND current status is final (Succeeded or Failed)
      const wasInProgress =
        isTesting ||
        previousStatus === 'Acknowledged' ||
        previousStatus === 'InProgress';

      // Show toast if we're testing and status transitions to final state
      // OR if status was in progress and transitions to final state
      if (
        wasInProgress &&
        (currentStatus === 'Succeeded' || currentStatus === 'Failed')
      ) {
        showConnectionToast(payer.name, currentStatus);
        // Clear testing state
        updatePayerState(payer.id, {
          isLocalTestingConnection: false,
        });
      }
    });

    // Update previous statuses ref
    previousStatusesRef.current = currentStatuses;
  }, [
    payerStatusData,
    payers,
    payerStates,
    updatePayerState,
    showConnectionToast,
  ]);

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-full vai-overflow-hidden">
      {/* Topbar */}
      <div className="vai-px-6 vai-py-5 vai-bg-white vai-border-b vai-text-primary vai-flex vai-justify-between vai-items-center vai-flex-shrink-0">
        <button
          onClick={handleBackToPatientList}
          className="vai-flex vai-items-center vai-gap-2 vai-text-primary hover:vai-opacity-80 vai-transition-opacity"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="vai-mr-2 vai-text-primary"
          />
          <span>
            <Trans>Back to Patient List</Trans>
          </span>
        </button>
      </div>

      {/* Page Content */}
      <div className="vai-flex-1 vai-overflow-hidden vai-bg-gray-50 vai-flex vai-flex-col vai-min-h-0">
        <div className="vai-p-6 vai-flex-shrink-0">
          {/* Title Section */}
          <div className="vai-flex vai-items-center vai-justify-between">
            <h1 className="vai-text-2xl vai-font-semibold vai-text-gray-900">
              <Trans>Auto Verify Credentials</Trans>
            </h1>
            <div className="vai-flex vai-items-center vai-gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  history.push(routes.PRACTICE_CONFIGURATION_SETTINGS)
                }
              >
                <FontAwesomeIcon icon={faGear} />
                <Trans>Settings</Trans>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="vai-flex vai-flex-1 vai-overflow-auto vai-px-6 vai-pb-6 vai-min-h-0 vai-gap-6">
          <div className="vai-w-1/3 vai-flex-shrink-0 vai-overflow-auto">
            <div className="vai-bg-white vai-rounded-lg vai-border vai-border-border vai-h-full vai-overflow-hidden">
              <PayerListSidebar
                onPayerClick={handlePayerClick}
                selectedPayerId={selectedPayerId}
                testingPayerIds={testingPayerIds}
                payerStatusData={payerStatusData}
                loadingStates={loadingStates}
              />
            </div>
          </div>
          <div className="vai-flex-1 vai-min-w-0 vai-overflow-auto">
            <div>
              <div className="vai-bg-white vai-rounded-lg vai-p-6 vai-border vai-border-border">
                {selectedPayer && (
                  <PayerHeader
                    payer={selectedPayer}
                    statusChipProps={statusChipProps}
                  />
                )}

                {!onboardingError && (isFailedStatus || connectionError) && (
                  <ConnectionErrorBanner />
                )}

                {selectedPayer && (
                  <ConnectionInstructions payer={selectedPayer} />
                )}

                {onboardingError ? (
                  <CredentialsFailedError />
                ) : isLoadingOnboarding ? (
                  <VideaUserInformationFormSkeleton />
                ) : (
                  <VideaUserInformationForm
                    onboardingData={onboardingResponse || undefined}
                    payerId={selectedPayerId || undefined}
                    localPassword={localPassword}
                    isSaving={beginOnboardingMutation.isLoading}
                    onSavePassword={handleSavePassword}
                    onSaveUsername={handleSaveUsername}
                    onSaveQuestions={handleSaveQuestions}
                  />
                )}

                {selectedPayer && (
                  <>
                    <ConnectionInstructions
                      payer={selectedPayer}
                      startNumber={3}
                    />
                    <TestConnectionButton
                      onTestConnection={handleTestConnection}
                      isLoading={isButtonLoading}
                      isDisabled={
                        isLoadingOnboarding ||
                        !!onboardingError ||
                        isButtonLoading
                      }
                    />
                    <Payer2FAMessages
                      key={selectedPayer.id}
                      payerName={selectedPayer.name}
                      messages={selectedPayerMessages}
                      isLoading={isMessagesLoading}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};
